import { useMsal } from '@azure/msal-react';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import {
  useHasPermission,
  usePermissions,
} from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import * as React from 'react';

import type {
  LibraryPackageWidgetMFEProps,
  LibraryPackageWidgetProps,
} from './LibraryPackageWidget.types';

const LibraryPackageWidget = React.memo(
  ({
    onLoad,
  }: Readonly<LibraryPackageWidgetProps>): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { permissions } = usePermissions();
    const canViewLibraryPackageWidget = useHasPermission(
      'ModuleTrain_View_Any'
    );

    const [RemoteComponent, setRemoteComponent] =
      React.useState<React.ComponentType<LibraryPackageWidgetMFEProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    React.useEffect(() => {
      if (!canViewLibraryPackageWidget) {
        onLoad?.();
        return;
      }

      const loadComponent = async (): Promise<void> => {
        try {
          const { default: Component } =
            await import('training_content_mfe/LibraryPackageWidget');
          setRemoteComponent(() => Component);
        } catch (error) {
          console.error('Error loading LibraryPackageWidget:', error);
          setLoadError(error as Error);
          onLoad?.();
        }
      };

      loadComponent();
    }, [canViewLibraryPackageWidget, onLoad]);

    if (!canViewLibraryPackageWidget || loadError) {
      return null;
    }

    if (!RemoteComponent) {
      return null;
    }

    return (
      <React.Suspense fallback={<ContentLoader />}>
        <RemoteComponent
          msalInstance={instance}
          user={authData}
          permissions={permissions}
          onLoad={onLoad}
        />
      </React.Suspense>
    );
  }
);

LibraryPackageWidget.displayName = 'LibraryPackageWidget';

export { LibraryPackageWidget };
